import React, { useEffect } from 'react';
import { XIcon, SparklesIcon, SendIcon, FilterIcon, MessageSquareIcon, PlusIcon } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import type { AiMessage, AiConversation } from './types';
interface AiDrawerProps {
  aiMessages: AiMessage[];
  aiHistory: AiConversation[];
  aiInput: string;
  setAiInput: (v: string) => void;
  aiLoading: boolean;
  aiTab: 'chat' | 'history';
  setAiTab: (tab: 'chat' | 'history') => void;
  aiMessagesEndRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  onSend: () => void;
  onApplyCohort: (docIds: string[]) => void;
  onStartNewChat: () => void;
  onLoadConversation: (conv: AiConversation) => void;
  totalFiltered: number;
}
export function AiDrawer({
  aiMessages,
  aiHistory,
  aiInput,
  setAiInput,
  aiLoading,
  aiTab,
  setAiTab,
  aiMessagesEndRef,
  onClose,
  onSend,
  onApplyCohort,
  onStartNewChat,
  onLoadConversation,
  totalFiltered
}: AiDrawerProps) {
  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (aiMessagesEndRef.current) {
      aiMessagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [aiMessages, aiLoading, aiMessagesEndRef]);
  return <div className="absolute top-0 right-0 bottom-0 w-[480px] bg-navy-light border-l border-navy-lighter flex flex-col shadow-2xl z-40" data-id="element-33">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-navy-lighter shrink-0" data-id="element-34">
        <div className="flex items-center gap-2" data-id="element-35">
          <div className="p-1.5 bg-amber/10 rounded-md" data-id="element-36">
            <SparklesIcon className="w-4 h-4 text-amber" data-id="element-37" />
          </div>
          <h2 className="font-serif text-white font-medium text-lg" data-id="element-38">Ask AI</h2>
          <span className="text-[10px] text-gray-400 bg-navy px-2 py-0.5 rounded-full ml-2 border border-navy-lighter" data-id="element-39">
            Asking across {totalFiltered.toLocaleString()} docs
          </span>
        </div>
        <div className="flex items-center gap-1" data-id="element-40">
          <Tooltip content="New Chat" position="bottom" data-id="element-41">
            <button onClick={onStartNewChat} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors" data-id="element-42">
              <PlusIcon className="w-4 h-4" data-id="element-43" />
            </button>
          </Tooltip>
          <Tooltip content="Close" shortcut="Esc" position="bottom" align="end" data-id="element-44">
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors ml-1" data-id="element-45">
              <XIcon className="w-4 h-4" data-id="element-46" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-navy-lighter shrink-0" data-id="element-47">
        <button onClick={() => setAiTab('chat')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative ${aiTab === 'chat' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-48">
          Current Chat
          {aiTab === 'chat' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-49" />}
        </button>
        <button onClick={() => setAiTab('history')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative ${aiTab === 'history' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-50">
          History
          {aiTab === 'history' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-51" />}
        </button>
      </div>

      {/* Content */}
      {aiTab === 'chat' ? <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0A1520]" data-id="element-52">
            {aiMessages.length === 0 && !aiLoading && <div className="h-full flex flex-col items-center justify-center text-center space-y-4" data-id="element-53">
                <div className="w-12 h-12 rounded-full bg-amber/10 flex items-center justify-center" data-id="element-54">
                  <SparklesIcon className="w-6 h-6 text-amber" data-id="element-55" />
                </div>
                <div className="space-y-1" data-id="element-56">
                  <p className="text-sm text-white font-medium" data-id="element-57">Ask anything about your documents</p>
                  <p className="text-xs text-gray-500" data-id="element-58">Get answers or filter to specific document sets</p>
                </div>
                <div className="flex flex-wrap gap-2 max-w-[320px] justify-center mt-2" data-id="element-59">
                  {['Which docs mention Amendment 3?', 'What is the earliest contract?', 'Find all emails from Sarah Chen', 'Show privileged communications'].map(prompt => <button key={prompt} onClick={() => setAiInput(prompt)} className="bg-navy border border-navy-lighter rounded-lg px-3 py-2 text-xs text-gray-400 hover:text-white hover:border-gray-600 cursor-pointer transition-colors text-left" data-id="element-60">
                      {prompt}
                    </button>)}
                </div>
              </div>}

            {aiMessages.map(msg => <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`} data-id="element-61">
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-amber text-navy rounded-tr-sm font-medium' : 'bg-navy border border-navy-lighter text-gray-200 rounded-tl-sm'}`} data-id="element-62">
                  {msg.content}
                </div>
                <span className="text-[10px] text-gray-500 mt-1.5 px-1" data-id="element-63">
                  {msg.timestamp}
                </span>

                {msg.resultType === 'cohort' && msg.cohortDocIds && <div className="mt-2 w-full max-w-[85%] bg-navy-light border border-amber/30 rounded-lg p-3 shadow-lg" data-id="element-64">
                    <div className="flex items-center justify-between mb-3" data-id="element-65">
                      <span className="text-xs text-amber font-medium flex items-center gap-1.5" data-id="element-66">
                        <FilterIcon className="w-3.5 h-3.5" data-id="element-67" />
                        {msg.cohortDocIds.length} documents found
                      </span>
                    </div>
                    <button onClick={() => onApplyCohort(msg.cohortDocIds!)} className="w-full py-2 bg-amber/10 hover:bg-amber/20 text-amber text-xs font-semibold rounded-md transition-colors border border-amber/20" data-id="element-68">
                      Apply as filter
                    </button>
                  </div>}
              </div>)}

            {aiLoading && <div className="flex items-start" data-id="element-69">
                <div className="bg-navy border border-navy-lighter rounded-xl rounded-tl-sm px-4 py-3.5 flex items-center gap-1.5" data-id="element-70">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-71" />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-72" />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-73" />
                </div>
              </div>}
            <div ref={aiMessagesEndRef} className="h-1" data-id="element-74" />
          </div>

          <div className="p-3 border-t border-navy-lighter shrink-0 bg-navy-light" data-id="element-75">
            <div className="flex gap-2" data-id="element-76">
              <textarea value={aiInput} onChange={e => setAiInput(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }} placeholder="Ask about these documents..." rows={2} className="flex-1 bg-navy border border-navy-lighter rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors resize-none" data-id="element-77" />
              <button onClick={onSend} disabled={!aiInput.trim() || aiLoading} className="self-end p-2.5 bg-amber hover:bg-amber-hover text-navy rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Send (Enter)" data-id="element-78">
                <SendIcon className="w-4 h-4" data-id="element-79" />
              </button>
            </div>
          </div>
        </> : <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0A1520]" data-id="element-80">
          {aiHistory.length === 0 ? <div className="text-center text-gray-500 text-sm mt-10" data-id="element-81">
              No history yet.
            </div> : aiHistory.map(conv => <div key={conv.id} className="bg-navy border border-navy-lighter rounded-lg p-3.5 hover:border-gray-500 transition-colors cursor-pointer group" onClick={() => onLoadConversation(conv)} data-id="element-82">
                <div className="flex items-start justify-between mb-2.5" data-id="element-83">
                  <h3 className="text-sm text-gray-200 font-medium line-clamp-2 pr-4 group-hover:text-amber transition-colors" data-id="element-84">
                    {conv.title}
                  </h3>
                  <span className="text-[10px] text-gray-500 shrink-0" data-id="element-85">
                    {conv.createdAt}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400" data-id="element-86">
                  <span className="flex items-center gap-1.5" data-id="element-87">
                    <MessageSquareIcon className="w-3.5 h-3.5" data-id="element-88" />
                    {conv.messages.length} msgs
                  </span>
                  {conv.cohortDocIds && <span className="flex items-center gap-1.5 text-amber/80" data-id="element-89">
                      <FilterIcon className="w-3.5 h-3.5" data-id="element-90" />
                      {conv.cohortDocIds.length} docs
                    </span>}
                </div>
                {conv.cohortDocIds && <button onClick={e => {
          e.stopPropagation();
          onApplyCohort(conv.cohortDocIds!);
        }} className="mt-3.5 w-full py-1.5 bg-navy-lighter hover:bg-navy-hover text-gray-300 text-xs font-medium rounded transition-colors border border-navy-lighter" data-id="element-91">
                    Re-apply filter
                  </button>}
              </div>)}
        </div>}
    </div>;
}